import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Card,
  Checkbox,
  Col,
  ConfigProvider,
  Divider,
  Flex,
  Input as AntdInput,
  Modal,
  Row,
  Space,
  Button,
  Tabs,
} from "antd";
import { DatePicker, Input, Select } from "components/form";
import { FilePdfOutlined, DownloadOutlined } from "@ant-design/icons";
import { formatDate } from "utils/formatter";
import StatusTag from "components/elements/TagCustom/StatusTag";
import { ReactComponent as PDFIcon } from "../../../assets/icon/pdfIcon.svg";
import { ReactComponent as DownloadICon } from "../../../assets/icon/downloadIcon.svg";
import schema, {
  initialValues as INITIAL_VALUES,
  DEFAULT_WARNING_LETTER_REASONS,
  OTHER_REASON_ID,
} from "../schemas/warningLetterSchema";
import { MY_REQUEST_TYPE } from "../schemas/myRequestSchema";
import * as services from "../services/myDocumentApi";

const { TextArea } = AntdInput;

const MODAL_TYPE = {
  CREATE: "create",
  EDIT: "edit",
  VIEW: "view",
};

function toDate(value) {
  return value ? dayjs(value) : null;
}

function getEmployeePosition(found, isEnglish) {
  if (!found) return "";
  if (isEnglish) {
    return found.positionNameEn || found.position || found.positionName || "";
  }
  return found.position || found.positionName || found.positionNameEn || "";
}

function resolvePosition(obj = {}, isEnglish) {
  return (isEnglish
    ? obj.positionNameEn || obj.positionName
    : obj.positionName || obj.positionNameEn) || "";
}

function buildInitialValues(initialValues = {}, isEnglish) {
  return {
    ...INITIAL_VALUES,
    ...initialValues,
    position: initialValues.position || resolvePosition(initialValues, isEnglish),
    incidentStartDate: toDate(initialValues?.incidentStartDate),
    incidentEndDate: toDate(initialValues?.incidentEndDate),
    issueDate: toDate(initialValues?.issueDate),
    subjectName: initialValues.subjectName || "",
    authorizedSigner: {
      ...INITIAL_VALUES.authorizedSigner,
      ...initialValues?.authorizedSigner,
      position: resolvePosition(initialValues?.authorizedSigner, isEnglish),
      signDate: toDate(initialValues?.authorizedSigner?.signDate),
    },
    supervisor: {
      ...INITIAL_VALUES.supervisor,
      ...initialValues?.supervisor,
      position: resolvePosition(initialValues?.supervisor, isEnglish),
      signDate: toDate(initialValues?.supervisor?.signDate),
    },
    witness: {
      ...INITIAL_VALUES.witness,
      ...initialValues?.witness,
      position: resolvePosition(initialValues?.witness, isEnglish),
      signDate: toDate(initialValues?.witness?.signDate),
    },
    acknowledgedEmployee: {
      ...INITIAL_VALUES.acknowledgedEmployee,
      ...initialValues?.acknowledgedEmployee,
      receivedDate: toDate(initialValues?.acknowledgedEmployee?.receivedDate),
    },
  };
}

function formatDateTime(dateString, isEnglish) {
  if (!dateString) return;
  const date = dayjs(dateString);
  return date.format(isEnglish ? "DD/MM/YYYY HH:mm" : `DD/MM/${date.year() + 543} HH:mm`);
}

function getItem(label, key) {
  return { label, key };
}

function PersonFields({ prefix, label, values, setFieldValue, employeeOptions, disabled, isEnglish, t }) {
  const person = values?.[prefix] || {};
  return (
    <Col span={8}>
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <div style={{ fontWeight: 500 }}>{label}</div>
        <Select
          disabled={disabled}
          name={`${prefix}.employeeId`}
          label={t("name-surname") || "ชื่อ-นามสกุล"}
          placeholder={t("common.pleaseSelect") || "กรุณาเลือก"}
          size="large"
          required
          showSearch
          optionFilterProp="label"
          options={employeeOptions}
          onChange={(value) => {
            const found = employeeOptions.find((o) => o.value === value);
            setFieldValue(`${prefix}.employeeId`, value);
            setFieldValue(`${prefix}.position`, getEmployeePosition(found, isEnglish));
          }}
        />
        <Input
          disabled
          name={`${prefix}.position`}
          label={t("empolyeePosition") || "ตำแหน่ง"}
          placeholder="จะแสดงอัตโนมัติเมื่อเลือกพนักงาน"
          size="large"
        />
        <DatePicker
          disabled={disabled || person.unspecified}
          name={`${prefix}.signDate`}
          label={t("warning_letter.sign_date") || "วันที่ลงนาม"}
          placeholder={t("warning_letter.day_monyh_year")}
          required
          size="large"
          format={formatDate()}
        />
        <Checkbox
          disabled={disabled}
          checked={!!person.unspecified}
          onChange={(e) => setFieldValue(`${prefix}.unspecified`, e.target.checked)}
        >
          {t("warning_letter.not_specified_yet") || "ยังไม่ระบุข้อมูล"}
        </Checkbox>
      </Space>
    </Col>
  );
}

function WarningLetterForm({
  className = "",
  initialValues = {},
  employeeOptions = [],
  reasonOptions = [],
  companyRegulationOptions = [],
  modalType = "view",
  isApprovalView = false,
  onDraft = () => { },
  onCancel = () => { },
  onDownload = () => { },
  isDownloading = false,
  onSubmit = () => { },
  onReject = () => { },
  onPreviewPdf = () => { },
}) {
  const { t, i18n } = useTranslation();
  const isView = modalType === "view";
  const isEnglish = (i18n?.language || "").toLowerCase().startsWith("en");

  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);

  const [selectedMenu, setMenu] = useState(MY_REQUEST_TYPE.DETAIL);
  const [currentWorkflow, setCurrentWorkflow] = useState([]);
  const [listEmployees, setListEmployees] = useState({});
  const [openListEmployee, setOpenListEmployee] = useState(false);

  const reasonList = DEFAULT_WARNING_LETTER_REASONS;

  const formInitialValues = useMemo(
    () => buildInitialValues(initialValues, isEnglish),
    [initialValues, isEnglish]
  );

  const getWorkflow = useCallback(async () => {
    let data = [];
    if (modalType === MODAL_TYPE.VIEW) {
      data = await services.getWorkflowByRequestId({
        flowRequestId: initialValues.warningLetterRequestId,
        companyId: currentCompany,
        employeeId: currentEmployee,
      });
      setCurrentWorkflow(data?.data?.approvalSequence);
    }
  }, [currentCompany, currentEmployee, initialValues.warningLetterRequestId, modalType]);

  useEffect(() => {
    getWorkflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, currentEmployee, initialValues, modalType]);

  const handleMenuChange = useCallback((e) => {
    setMenu(e);
  }, [setMenu]);

  const MENU = [
    getItem(t("leave_type.label.detail"), MY_REQUEST_TYPE.DETAIL),
    (modalType === MODAL_TYPE.VIEW && initialValues?.approveStatusEn !== 'Draft')
      ? getItem(t("leave_type.label.approvalSequence"), MY_REQUEST_TYPE.APPROVALSEQ)
      : null,
  ].filter(Boolean);
  const language = i18n.language === "en" ? "En" : "";

  return (
    <Formik
      enableReinitialize
      initialValues={formInitialValues}
      validationSchema={schema}
      onSubmit={(values, { resetForm }) => { initialValues.reject == 'reject' ? onReject(values) : onSubmit(values, resetForm) }}
    >
      {({ values = {}, setFieldValue, resetForm, submitForm }) => {
        const detailLength = values?.incidentDetail?.length || 0;
        return (
          <Form className={className}>
            <ConfigProvider
              theme={{
                components: {
                  Tabs: {
                    itemHoverColor: "var(--tab-color)",
                    itemActiveColor: "var(--tab-color)",
                  },
                },
              }}
            >
              <Tabs items={MENU} onChange={handleMenuChange} />
            </ConfigProvider>

            {selectedMenu === MY_REQUEST_TYPE.DETAIL && (
              <>
                <Divider style={{ marginTop: 0 }} />
                <Space size={24} direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>
                      {t("warning_letter.employee_and_document_info") || "ข้อมูลพนักงานและเอกสาร"}
                    </div>
                    <Row gutter={[24, 12]}>
                      <Col span={12}>
                        <Select
                          disabled={isView}
                          name="employeeId"
                          required
                          label={`${t("name-surname") || "ชื่อ-นามสกุลพนักงาน"}`}
                          placeholder={t("common.pleaseSelect") || "กรุณาเลือก"}
                          size="large"
                          showSearch
                          optionFilterProp="label"
                          options={employeeOptions}
                          onChange={(value) => {
                            const found = employeeOptions.find((o) => o.value === value);
                            setFieldValue("employeeId", value);
                            setFieldValue("position", getEmployeePosition(found, isEnglish));
                          }}
                        />
                      </Col>
                      <Col span={12}>
                        <Input
                          disabled
                          name="position"
                          label={t("empolyeePosition") || "ตำแหน่ง"}
                          placeholder="จะแสดงอัตโนมัติเมื่อเลือกพนักงาน"
                          size="large"
                        />
                      </Col>
                    </Row>
                  </div>

                  <Row gutter={[24, 12]}>
                    <Col span={8}>
                      <DatePicker
                        disabled={isView}
                        name="incidentStartDate"
                        required
                        label={`${t("warning_letter.label.incident_start_date") || "วันที่เริ่มเหตุการณ์"}`}
                        placeholder={t("warning_letter.day_monyh_year")}
                        size="large"
                        format={formatDate()}
                      />
                    </Col>
                    <Col span={8}>
                      <DatePicker
                        disabled={isView}
                        name="incidentEndDate"
                        required
                        label={`${t("warning_letter.incident_end_date") || "วันที่สิ้นสุดเหตุการณ์"}`}
                        placeholder={t("warning_letter.day_monyh_year")}
                        size="large"
                        format={formatDate()}
                      />
                    </Col>
                    <Col span={8}>
                      <DatePicker
                        disabled={isView}
                        name="issueDate"
                        required
                        label={`${t("warning_letter.issue_date") || "วันที่ออกหนังสือเตือน"}`}
                        placeholder={t("warning_letter.day_monyh_year")}
                        size="large"
                        format={formatDate()}
                      />
                    </Col>
                  </Row>

                  <Row gutter={[24, 12]}>
                    <Col span={24}>
                      {modalType === "view" ? (
                        <AntdInput
                          disabled
                          label={t("warning_letter.subject")}
                          value={initialValues.subjectName}
                          placeholder={t("common.pleaseSelect") || "กรุณาเลือก"}
                        />
                      ) : (
                        <Select
                          name="subject"
                          options={companyRegulationOptions}
                          label={t("warning_letter.subject")}
                          placeholder={t("common.pleaseSelect") || "กรุณาเลือก"}
                        />
                      )}
                    </Col>
                  </Row>

                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>
                      {t("warning_letter.reasons") || "เหตุผลในการออกหนังสือเตือน"}
                      <span style={{ fontWeight: 400, color: "#667085" }}>
                        {" "}
                        ({t("common.select_multiple") || "เลือกได้มากกว่า 1 ข้อ"})
                      </span>
                    </div>
                    <Row gutter={[16, 12]}>
                      {reasonList.map((reason) => (
                        <Col span={8} key={reason.id}>
                          <Checkbox
                            disabled={isView}
                            checked={(values.reasons || []).includes(reason.id)}
                            onChange={(e) => {
                              const current = values.reasons || [];
                              const next = e.target.checked
                                ? [...current, reason.id]
                                : current.filter((r) => r !== reason.id);
                              setFieldValue("reasons", next);
                            }}
                          >
                            {t(reason.label)}
                          </Checkbox>
                        </Col>
                      ))}
                      <Col span={8}>
                        <Space direction="horizontal" size={8} style={{ width: "100%" }}>
                          <Checkbox
                            disabled={isView}
                            checked={(values.reasons || []).includes(OTHER_REASON_ID)}
                            onChange={(e) => {
                              const current = values.reasons || [];
                              const next = e.target.checked
                                ? [...current, OTHER_REASON_ID]
                                : current.filter((r) => r !== OTHER_REASON_ID);
                              setFieldValue("reasons", next);
                            }}
                          >
                            {t("common.other") || "อื่นๆ"}
                          </Checkbox>
                        </Space>
                      </Col>
                      {(values.reasons || []).includes(OTHER_REASON_ID) && (
                        <Col span={24}>
                          <AntdInput
                            disabled={isView}
                            value={values.otherReasonDetail}
                            placeholder={t("common.please_specify") || "ระบุรายละเอียด"}
                            size="large"
                            onChange={(e) => setFieldValue("otherReasonDetail", e.target.value)}
                          />
                        </Col>
                      )}
                    </Row>
                  </div>

                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>
                      {`${t("warning_letter.incident_detail") || "รายละเอียดการกระทำ / ข้อเท็จจริง"}`}
                    </div>
                    <TextArea
                      disabled={isView}
                      value={values.incidentDetail}
                      onChange={(e) => setFieldValue("incidentDetail", e.target.value)}
                      maxLength={300}
                      rows={4}
                      placeholder={
                        t("warning_letter.label.incident_detail_placeholder") ||
                        "ระบุรายละเอียดของเหตุการณ์ เช่น วัน เวลา สถานที่ที่การกระทำที่ฝ่าฝืน รายละเอียดเพิ่มเติมอื่นๆ"
                      }
                    />
                    <div style={{ textAlign: "right", color: "#667085", fontSize: 12 }}>
                      {detailLength} / 300
                    </div>
                  </div>

                  <Divider style={{ margin: 0 }} />

                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>
                      {t("warning_letter.authorized_signer") || "ผู้มีอำนาจลงนามในหนังสือเตือน (ในนามบริษัท)"}
                    </div>
                    <Row gutter={[24, 12]}>
                      <Col span={8}>
                        <Select
                          disabled={isView}
                          name="authorizedSigner.employeeId"
                          required
                          label={`${t("warning_letter.signer_name") || "ชื่อผู้ลงนาม"}`}
                          placeholder={t("common.pleaseSelect") || "เลือกชื่อผู้ลงนาม"}
                          size="large"
                          showSearch
                          optionFilterProp="label"
                          options={employeeOptions}
                          onChange={(value) => {
                            const found = employeeOptions.find((o) => o.value === value);
                            setFieldValue("authorizedSigner.employeeId", value);
                            setFieldValue("authorizedSigner.position", getEmployeePosition(found, isEnglish));
                          }}
                        />
                      </Col>
                      <Col span={8}>
                        <Input
                          disabled
                          name="authorizedSigner.position"
                          required
                          label={`${t("empolyeePosition") || "ตำแหน่ง"}`}
                          placeholder="จะแสดงอัตโนมัติ"
                          size="large"
                        />
                      </Col>
                      <Col span={8}>
                        <Space direction="vertical" size={8} style={{ width: "100%" }}>
                          <DatePicker
                            disabled={isView || values.authorizedSigner?.useIssueDateAsSignDate}
                            name="authorizedSigner.signDate"
                            required
                            label={`${t("warning_letter.sign_date") || "วันที่ลงนาม"}`}
                            placeholder={t("warning_letter.day_monyh_year")}
                            size="large"
                            format={formatDate()}
                          />
                          <Checkbox
                            disabled={isView}
                            checked={!!values.authorizedSigner?.useIssueDateAsSignDate}
                            onChange={(e) => {
                              setFieldValue("authorizedSigner.useIssueDateAsSignDate", e.target.checked);
                              if (e.target.checked) {
                                setFieldValue("authorizedSigner.signDate", values.issueDate);
                              }
                            }}
                          >
                            {t("warning_letter.use_issue_date_as_sign_date") ||
                              "ใช้วันที่ออกหนังสือเตือนเป็นวันที่ลงนาม"}
                          </Checkbox>
                        </Space>
                      </Col>
                    </Row>
                  </div>

                  <Divider style={{ margin: 0 }} />

                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>
                      {t("warning_letter.acknowledged_persons") || "ผู้เกี่ยวข้องในการรับทราบหนังสือเตือน"}
                    </div>
                    <Row gutter={[24, 12]}>
                      <PersonFields
                        prefix="supervisor"
                        label={t("warning_letter.supervisor") || "ผู้บังคับบัญชา / หัวหน้างาน"}
                        values={values}
                        setFieldValue={setFieldValue}
                        employeeOptions={employeeOptions}
                        disabled={isView}
                        isEnglish={isEnglish}
                        t={t}
                      />
                      <PersonFields
                        prefix="witness"
                        label={t("warning_letter.witness") || "พยานฝ่ายบริษัท"}
                        values={values}
                        setFieldValue={setFieldValue}
                        employeeOptions={employeeOptions}
                        disabled={isView}
                        isEnglish={isEnglish}
                        t={t}
                      />
                      <Col span={8}>
                        <Space direction="vertical" size={8} style={{ width: "100%" }}>
                          <div style={{ fontWeight: 500 }}>
                            {t("warning_letter.acknowledged_employee") || "ผู้รับทราบ (พนักงาน)"}
                          </div>
                          {/* <div style={{ fontSize: 12, color: "#667085" }}>
                            {t("warning_letter.acknowledged_employee_hint") ||
                              "พนักงานจะลงลายมือชื่อรับทราบด้วยตนเองเมื่อแจ้งในเอกสาร"}
                          </div> */}
                          <DatePicker
                            disabled={isView || values.acknowledgedEmployee?.notSignedYet}
                            name="acknowledgedEmployee.receivedDate"
                            label={t("warning_letter.label.received_date") || "วันที่รับทราบ"}
                            placeholder={t("warning_letter.day_monyh_year")}
                            size="large"
                            format={formatDate()}
                          />
                          <Checkbox
                            disabled={isView}
                            checked={!!values.acknowledgedEmployee?.notSignedYet}
                            onChange={(e) =>
                              setFieldValue("acknowledgedEmployee.notSignedYet", e.target.checked)
                            }
                          >
                            {t("warning_letter.not_signed_yet") || "ยังไม่ได้ลงนาม"}
                          </Checkbox>
                        </Space>
                      </Col>
                    </Row>
                  </div>
                  {isView && initialValues?.approveStatusEn === "Approved" && (
                    <>
                      <Divider style={{ margin: 0 }} />
                      <Flex vertical gap={8} style={{ width: "100%" }}>
                        <span style={{ fontWeight: 600 }}>
                          {t("payment.label.download_doc") || "ดาวน์โหลดเอกสาร"}
                        </span>
                        <Card
                          style={{ backgroundColor: "#F5F6F7", border: "1px solid #E4E7EC", borderRadius: "12px" }}
                          styles={{ body: { padding: "16px" } }}
                        >
                          <Row style={{ width: "100%" }} align="middle">
                            <Col span={16}>
                              <Flex gap={16} align="center">
                                <PDFIcon />
                                <Flex vertical>
                                  <span style={{ fontWeight: "bold", color: "#42526D" }}>
                                    {`${t("warning_letter.header") || "หนังสือเตือน"}_${initialValues?.sequence || initialValues?.warningLetterRequestId || ""}`}
                                  </span>
                                  <span style={{ color: "#42526D" }}>{t("common.pdf_file") || "ไฟล์ PDF"}</span>
                                </Flex>
                              </Flex>
                            </Col>
                            <Col span={8} style={{ alignContent: "center", justifyItems: "flex-end" }}>
                              <Button
                                type="default"
                                style={{ height: "40px", borderRadius: "8px", display: "flex", alignItems: "center" }}
                                onClick={() => onDownload(initialValues)}
                                icon={
                                  !isDownloading ? (
                                    <DownloadICon style={{ fontSize: 16 }} />
                                  ) : (
                                    <span style={{ width: 16, height: 16 }} />
                                  )
                                }
                                loading={isDownloading}
                                disabled={isDownloading}
                              >
                                <span style={{ color: "#59687F", fontWeight: "bold" }}>{t("common.download") || "ดาวน์โหลด"}</span>
                              </Button>
                            </Col>
                          </Row>
                        </Card>
                      </Flex>
                    </>
                  )}
                  <Row justify="end">
                    <Space direction="horizontal" size={12}>
                      {!isApprovalView && (
                        <Button
                          id="mst__cancelBtn"
                          type="default"
                          onClick={() => {
                            resetForm();
                            onCancel();
                          }}
                          style={{ height: "40px", borderRadius: "8px" }}
                        >
                          {t("common.cancel")}
                        </Button>
                      )}
                      {isApprovalView ? (
                        <>
                          <Button
                            type="default"
                            onClick={() => onReject(values)}
                            style={{ height: "40px", borderRadius: "8px" }}
                          >
                            {t("warning_letter.reject") || "ไม่อนุมัติ"}
                          </Button>
                          <Button
                            type="primary"
                            onClick={() => onSubmit(values)}
                            style={{
                              height: "40px",
                              borderRadius: "8px",
                              backgroundColor: "var(--button-color)",
                              color: "white",
                              boxShadow: "none",
                            }}
                          >
                            {t("warning_letter.approve") || "อนุมัติ"}
                          </Button>
                        </>
                      ) : isView ? null : (
                        <>
                          <Button
                            type="default"
                            onClick={() => onDraft(values)}
                            style={{ height: "40px", borderRadius: "8px" }}
                          >
                            {t("common.draft")}
                          </Button>
                          <Button
                            type="default"
                            onClick={() => onPreviewPdf(values)}
                            style={{ height: "40px", borderRadius: "8px" }}
                          >
                            {t("common.preview_pdf") || "ดูตัวอย่าง PDF"}
                          </Button>
                          <Button
                            type="primary"
                            onClick={submitForm}
                            style={{
                              height: "40px",
                              borderRadius: "8px",
                              backgroundColor: "var(--button-color)",
                              color: "white",
                              boxShadow: "none",
                            }}
                          >
                            {t("warning_letter.confirm_and_create") || "ยืนยันและสร้างหนังสือเตือน"}
                          </Button>
                        </>
                      )}
                    </Space>
                  </Row>
                </Space>
              </>
            )}

            {selectedMenu === MY_REQUEST_TYPE.APPROVALSEQ && (
              <>
                {Array.isArray(currentWorkflow) &&
                  currentWorkflow?.map((workflow, index) => (
                    <div key={index}>
                      <label>{`${t('leave_type.label.sequence')} ${index + 1}`}</label>
                      <Card size="small" style={{ border: "1px solid #000" }}>
                        <Row>
                          <Col span={16}>
                            <label style={{ color: "#667085" }}>{`${t('leave_type.label.approver')}`}</label>
                          </Col>
                          <Col span={8}>
                            <span style={{ color: "#667085", display: "flex", justifyContent: "flex-end" }}>{t("leave_type.label.approvalDate")}</span>
                          </Col>
                          <Col span={16}>
                            <span>{workflow?.listEmployees?.slice(0, 3).join(" , ")}</span>
                            <> </>
                            {workflow?.listEmployees?.length > 3 ? (
                              <a
                                onClick={() => {
                                  setOpenListEmployee(true);
                                  setListEmployees(workflow?.listEmployees);
                                }}
                                style={{
                                  display: "inline-block",
                                  borderRadius: "36px",
                                  padding: "4px 12px",
                                  fontSize: "0.7vw",
                                  color: "#fff",
                                  background: "var(--primary-color)",
                                  textDecoration: "none",
                                  fontWeight: "bold",
                                  border: "1px solid var(--primary-color)",
                                  cursor: "pointer",
                                }}
                              >
                                {`+ ${workflow?.listEmployees?.length - 3} ...`}
                              </a>
                            ) : null}
                          </Col>
                          <Col span={8}>
                            <span style={{ display: "flex", justifyContent: "flex-end" }}>{workflow?.approveDate ? formatDateTime(workflow?.approveDate, isEnglish) : "-"}</span>
                          </Col>
                        </Row>
                        <Row>
                          <Col span={16} style={{ marginTop: '10px' }}>
                            <label>{workflow?.workFlowtype}</label>
                          </Col>
                          <Col span={8} style={{ marginTop: '10px' }}>
                            <label style={{ display: "flex", justifyContent: "flex-end" }}>
                              <StatusTag value={(workflow?.["approveStatus" + language])} />
                            </label>
                          </Col>
                        </Row>
                      </Card>
                    </div>
                  ))}
              </>
            )
            }

            {openListEmployee && (
              <Modal
                title={t("leave_type.label.approver")}
                open={openListEmployee}
                onCancel={() => setOpenListEmployee(false)}
                footer={false}
                width="510px"
                maskClosable={false}
              >
                <Card size="small" style={{ border: "1px solid #000" }}>
                  {listEmployees?.map((employee, index) => (
                    <label key={index}>
                      {employee}
                      <br />
                    </label>
                  ))}
                </Card>
              </Modal>
            )}
          </Form>
        );
      }}
    </Formik >
  );
}

export default WarningLetterForm;